import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Autocomplete,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  CardGiftcard as GiftIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

/** Audience a gift promo applies to. Older API builds omit the field → treat as 'public'. */
type GiftAudience = 'public' | 'vip';

interface GiftPromo {
  id: number;
  label: string | null;
  audience?: GiftAudience | null;
  min_order: number | string;
  gift_sku: string | null;
  gift_title: string | null;
  gift_image_url: string | null;
  gift_qty: number;
  gift_total_cap: number | string | null;
  is_active: boolean;
  starts_at: string;
  expires_at: string;
  created_at: string;
  live: boolean;
  given: number;
  remaining: number | null;
  soldOut: boolean;
}

interface GiftListResponse {
  gifts: GiftPromo[];
}

interface ProductOption {
  handle: string;
  shopify_id: number | string;
  title: string;
  shopify_variant_id: number | string;
  sku: string | null;
  image_src: string | null;
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

/** Backward compat while the backend rolls out: a missing audience means 'public'. */
function audienceOf(promo: GiftPromo): GiftAudience {
  return promo.audience === 'vip' ? 'vip' : 'public';
}

function toNumber(v: number | string | null): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** ISO timestamp → YYYY/MM/DD in Asia/Taipei for table display. */
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('zh-TW', {
      timeZone: 'Asia/Taipei',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** ISO timestamp → YYYY-MM-DD in Asia/Taipei for <input type="date"> prefill. */
function toDateInputValue(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });
  } catch {
    return '';
  }
}

/** YYYY-MM-DD date input → full-day boundary timestamps in Taipei time. */
function startOfDayTaipei(date: string): string {
  return `${date}T00:00:00+08:00`;
}
function endOfDayTaipei(date: string): string {
  return `${date}T23:59:59+08:00`;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

const GiftPromosPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [gifts, setGifts] = useState<GiftPromo[]>([]);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // Edit dialog state
  const [editTarget, setEditTarget] = useState<GiftPromo | null>(null);
  const [editCap, setEditCap] = useState('');
  const [editThreshold, setEditThreshold] = useState('');
  const [editQty, setEditQty] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [product, setProduct] = useState<ProductOption | null>(null);
  const [productQuery, setProductQuery] = useState('');
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [productSearching, setProductSearching] = useState(false);
  const [newThreshold, setNewThreshold] = useState('');
  const [newCap, setNewCap] = useState('');
  const [newQty, setNewQty] = useState('1');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newAudience, setNewAudience] = useState<GiftAudience>('public');
  const [createSubmitting, setCreateSubmitting] = useState(false);

  // Split one fetch into the two rendered sections.
  const publicGifts = useMemo(() => gifts.filter((g) => audienceOf(g) === 'public'), [gifts]);
  const vipGifts = useMemo(() => gifts.filter((g) => audienceOf(g) === 'vip'), [gifts]);

  // --------------------------------------------------------------------------
  // Load
  // --------------------------------------------------------------------------

  const fetchGifts = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const { data } = await api.get<GiftListResponse>('/api/promos/gift/admin');
        setGifts(data.gifts || []);
      } catch (err: any) {
        enqueueSnackbar(err?.response?.data?.error || '載入滿額贈清單失敗', {
          variant: 'error',
        });
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    fetchGifts();
  }, [fetchGifts]);

  // --------------------------------------------------------------------------
  // Product search (create dialog) — debounced
  // --------------------------------------------------------------------------

  useEffect(() => {
    const q = productQuery.trim();
    if (q.length < 2) {
      setProductOptions([]);
      return;
    }
    let cancelled = false;
    setProductSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get<{ products: ProductOption[] }>(
          '/api/promos/gift/admin/product-search',
          { params: { q } }
        );
        if (!cancelled) setProductOptions(data.products || []);
      } catch {
        if (!cancelled) setProductOptions([]);
      } finally {
        if (!cancelled) setProductSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [productQuery]);

  // --------------------------------------------------------------------------
  // Toggle active
  // --------------------------------------------------------------------------

  const toggleActive = async (promo: GiftPromo) => {
    setTogglingId(promo.id);
    try {
      await api.patch(`/api/promos/gift/admin/${promo.id}`, {
        is_active: !promo.is_active,
      });
      enqueueSnackbar(promo.is_active ? '已停用滿額贈' : '已啟用滿額贈', {
        variant: 'success',
      });
      fetchGifts(true);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || '切換狀態失敗', { variant: 'error' });
    } finally {
      setTogglingId(null);
    }
  };

  // --------------------------------------------------------------------------
  // Edit dialog
  // --------------------------------------------------------------------------

  const openEditDialog = (promo: GiftPromo) => {
    const cap = toNumber(promo.gift_total_cap);
    setEditCap(cap != null ? String(cap) : '');
    setEditThreshold(String(toNumber(promo.min_order) ?? ''));
    setEditQty(String(promo.gift_qty ?? 1));
    setEditStart(toDateInputValue(promo.starts_at));
    setEditEnd(toDateInputValue(promo.expires_at));
    setEditLabel(promo.label || '');
    setEditTarget(promo);
  };

  const closeEditDialog = () => {
    if (editSubmitting) return;
    setEditTarget(null);
  };

  const editThresholdNum = parseInt(editThreshold, 10);
  const editQtyNum = parseInt(editQty, 10);
  const editCapNum = editCap.trim() === '' ? null : parseInt(editCap, 10);
  const editValid =
    Number.isFinite(editThresholdNum) &&
    editThresholdNum > 0 &&
    Number.isFinite(editQtyNum) &&
    editQtyNum > 0 &&
    (editCapNum === null || (Number.isFinite(editCapNum) && editCapNum >= 0)) &&
    editStart !== '' &&
    editEnd !== '';

  const submitEdit = async () => {
    if (!editTarget || !editValid) return;
    setEditSubmitting(true);
    try {
      await api.patch(`/api/promos/gift/admin/${editTarget.id}`, {
        min_order: editThresholdNum,
        cap: editCapNum,
        qty: editQtyNum,
        starts_at: startOfDayTaipei(editStart),
        expires_at: endOfDayTaipei(editEnd),
        label: editLabel.trim() || null,
      });
      enqueueSnackbar('已更新滿額贈設定', { variant: 'success' });
      setEditTarget(null);
      fetchGifts(true);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || '更新失敗', { variant: 'error' });
    } finally {
      setEditSubmitting(false);
    }
  };

  // --------------------------------------------------------------------------
  // Create dialog
  // --------------------------------------------------------------------------

  const openCreateDialog = () => {
    setProduct(null);
    setProductQuery('');
    setProductOptions([]);
    setNewThreshold('');
    setNewCap('');
    setNewQty('1');
    setNewStart(toDateInputValue(new Date().toISOString()));
    setNewEnd('');
    setNewLabel('');
    setNewAudience('public');
    setCreateOpen(true);
  };

  const closeCreateDialog = () => {
    if (createSubmitting) return;
    setCreateOpen(false);
  };

  const newThresholdNum = parseInt(newThreshold, 10);
  const newQtyNum = parseInt(newQty, 10);
  const newCapNum = newCap.trim() === '' ? null : parseInt(newCap, 10);
  const createValid =
    product !== null &&
    Number.isFinite(newThresholdNum) &&
    newThresholdNum > 0 &&
    Number.isFinite(newQtyNum) &&
    newQtyNum > 0 &&
    (newCapNum === null || (Number.isFinite(newCapNum) && newCapNum > 0)) &&
    newStart !== '' &&
    newEnd !== '';

  const submitCreate = async () => {
    if (!createValid || !product) return;
    setCreateSubmitting(true);
    try {
      await api.post('/api/promos/gift/admin', {
        handle: product.handle,
        threshold: newThresholdNum,
        cap: newCapNum,
        qty: newQtyNum,
        starts_at: startOfDayTaipei(newStart),
        expires_at: endOfDayTaipei(newEnd),
        label: newLabel.trim() || undefined,
        audience: newAudience,
      });
      enqueueSnackbar('已建立滿額贈', { variant: 'success' });
      setCreateOpen(false);
      fetchGifts(true);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || '建立失敗', { variant: 'error' });
    } finally {
      setCreateSubmitting(false);
    }
  };

  // --------------------------------------------------------------------------
  // Render helpers
  // --------------------------------------------------------------------------

  const statusChip = (promo: GiftPromo) => {
    if (!promo.is_active) {
      return <Chip size="small" label="已停用" color="default" variant="outlined" />;
    }
    if (promo.live) {
      return <Chip size="small" label="LIVE" color="success" />;
    }
    return <Chip size="small" label="已排程或過期" color="warning" variant="outlined" />;
  };

  /** Shared table for both audience sections. `emptyNode` fills the row when there are none. */
  const renderGiftTable = (rows: GiftPromo[], emptyNode: React.ReactNode) => (
    <Paper>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 120 }}>狀態</TableCell>
              <TableCell>贈品</TableCell>
              <TableCell sx={{ width: 120 }} align="right">
                門檻
              </TableCell>
              <TableCell sx={{ width: 130 }} align="right">
                已送出 / 上限
              </TableCell>
              <TableCell sx={{ width: 90 }} align="right">
                剩餘
              </TableCell>
              <TableCell sx={{ width: 200 }}>活動期間</TableCell>
              <TableCell sx={{ width: 210 }} align="right">
                操作
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {emptyNode}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((promo) => {
                const cap = toNumber(promo.gift_total_cap);
                return (
                  <TableRow key={promo.id} hover>
                    <TableCell>{statusChip(promo)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={promo.gift_image_url || undefined}
                          variant="rounded"
                          sx={{ width: 48, height: 48 }}
                        >
                          <GiftIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {promo.gift_title || '（未命名贈品）'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: {promo.gift_sku || '—'}
                            {promo.gift_qty > 1 ? ` · 每單 ${promo.gift_qty} 件` : ''}
                          </Typography>
                          {promo.label && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {promo.label}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">滿 NT${toNumber(promo.min_order) ?? '—'}</TableCell>
                    <TableCell align="right">
                      {promo.soldOut ? (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: 'error.main', fontWeight: 700 }}
                        >
                          送完 {promo.given} / {cap}
                        </Typography>
                      ) : (
                        <>
                          {promo.given} / {cap != null ? cap : '∞'}
                        </>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {promo.remaining != null ? promo.remaining : '∞'}
                    </TableCell>
                    <TableCell>
                      {formatDate(promo.starts_at)} ～ {formatDate(promo.expires_at)}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" variant="outlined" onClick={() => openEditDialog(promo)}>
                          調整數量
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color={promo.is_active ? 'warning' : 'success'}
                          disabled={togglingId === promo.id}
                          onClick={() => toggleActive(promo)}
                        >
                          {promo.is_active ? '停用' : '啟用'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">滿額贈管理</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          新增滿額贈
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        限量贈品，送完為止（無每月重置）。補貨方式：用「調整數量」提高上限。
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              公開滿額贈
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              所有顧客適用，門檻以「折扣前 Metanoia 服飾小計」計算。
            </Typography>
            {renderGiftTable(publicGifts, '尚無滿額贈活動')}
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              VIP 滿額贈
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              VIP 滿額門檻以「折扣後 storefront
              小計（不含 Outlet）」計算，與公開滿額贈的「折扣前 Metanoia 服飾小計」不同。
            </Typography>
            {renderGiftTable(
              vipGifts,
              '系統預設：滿 NT$2,500 送 [MdoC] 極致甦活泡沫洗面乳（M05）— 建立 VIP 滿額贈後改以此處設定為準。'
            )}
          </Box>
        </Stack>
      )}

      {/* Edit dialog */}
      <Dialog open={editTarget !== null} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>調整滿額贈</DialogTitle>
        <DialogContent>
          {editTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {editTarget.gift_title}（SKU: {editTarget.gift_sku || '—'}）· 已送出{' '}
              {editTarget.given} 件
            </Typography>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="數量上限（留空 = 不限量）"
              type="number"
              value={editCap}
              onChange={(e) => setEditCap(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: 0, step: 1 }}
              helperText={
                editTarget
                  ? `補貨：把上限調高即可繼續送（目前已送出 ${editTarget.given} 件）。`
                  : undefined
              }
            />
            <TextField
              label="門檻（滿 NT$）"
              type="number"
              value={editThreshold}
              onChange={(e) => setEditThreshold(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: 1, step: 1 }}
            />
            <TextField
              label="每單贈送件數"
              type="number"
              value={editQty}
              onChange={(e) => setEditQty(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: 1, step: 1 }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="開始日期"
                type="date"
                value={editStart}
                onChange={(e) => setEditStart(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="結束日期"
                type="date"
                value={editEnd}
                onChange={(e) => setEditEnd(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="活動名稱（選填）"
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              size="small"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={editSubmitting}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={submitEdit}
            disabled={editSubmitting || !editValid}
          >
            {editSubmitting ? '處理中…' : '儲存變更'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>新增滿額贈</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                適用對象（建立後無法更改）
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={newAudience}
                onChange={(_e, value) => {
                  if (value) setNewAudience(value as GiftAudience);
                }}
              >
                <ToggleButton value="public">公開</ToggleButton>
                <ToggleButton value="vip">VIP 專屬</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {newAudience === 'vip'
                  ? '僅 VIP 會員適用，門檻以折扣後 storefront 小計（不含 Outlet）計算。'
                  : '所有顧客適用，門檻以折扣前 Metanoia 服飾小計計算。'}
              </Typography>
            </Box>
            <Autocomplete<ProductOption>
              options={productOptions}
              value={product}
              onChange={(_e, value) => setProduct(value)}
              inputValue={productQuery}
              onInputChange={(_e, value) => setProductQuery(value)}
              loading={productSearching}
              filterOptions={(x) => x}
              isOptionEqualToValue={(opt, val) => opt.handle === val.handle}
              getOptionLabel={(opt) => opt.title || opt.handle}
              noOptionsText={
                productQuery.trim().length < 2 ? '輸入至少 2 個字搜尋商品' : '找不到商品'
              }
              renderOption={(props, opt) => (
                <li {...props} key={opt.handle}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      src={opt.image_src || undefined}
                      variant="rounded"
                      sx={{ width: 40, height: 40 }}
                    >
                      <GiftIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{opt.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        SKU: {opt.sku || '—'} · {opt.handle}
                      </Typography>
                    </Box>
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="贈品商品（搜尋名稱 / handle / SKU）"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {productSearching ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              label="門檻（滿 NT$）"
              type="number"
              value={newThreshold}
              onChange={(e) => setNewThreshold(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: 1, step: 1 }}
            />
            <TextField
              label="數量上限（留空 = 不限量）"
              type="number"
              value={newCap}
              onChange={(e) => setNewCap(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: 1, step: 1 }}
              helperText="限量送完為止；之後可用「調整數量」補貨。"
            />
            <TextField
              label="每單贈送件數"
              type="number"
              value={newQty}
              onChange={(e) => setNewQty(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: 1, step: 1 }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="開始日期"
                type="date"
                value={newStart}
                onChange={(e) => setNewStart(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="結束日期"
                type="date"
                value={newEnd}
                onChange={(e) => setNewEnd(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="活動名稱（選填，留空自動產生）"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              size="small"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={createSubmitting}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={submitCreate}
            disabled={createSubmitting || !createValid}
          >
            {createSubmitting ? '處理中…' : '建立'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GiftPromosPage;
